import React, { useEffect, useState } from "react";
import Image from "components/Elements/Image/Image.tsx";
import Icon from "components/Elements/Icon/Icon.tsx";
import apiRequest from "utils/ApiRequest/apiRequest.js";
import Button, { ButtonA } from "components/Elements/Button/Button.js";
import Cookies from "universal-cookie";
import config from "config";
import parse from "html-react-parser";
import LoadingImageElement from "components/Elements/LoadingContainerElement/LoadingImageElement.tsx";
import Authentication from "components/Reusable/Authentication/index.tsx";
import { useParams } from "react-router-dom";
import { useUser } from "contexts/userContext/userContext.js";
import { useContent } from "contexts/contentContext/contentContext.js";
import { useModal } from "contexts/modalContext/modalContext.js";
import { InviteSubUserCheckboxAgreement } from "components/Pages/SubUsers/InviteSubUserCheckboxAgreement.tsx";
import "./InvitationLinkCard.scss";

const InvitationLinkCard = ({ setJoin, setProfileLogo }) => {
  const { getTextByKey, getHtmlByKey, getPath } = useContent();
  const params = useParams();
  const { getUserData, isLoggedIn, logout, setUserDataProp } = useUser();
  const [subUsersData, setSubUsersData] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const { setModal } = useModal();
  const cookies = new Cookies();
  const [isJoinAllowed, setIsJoinAllowed] = useState();
  const userData = getUserData() || {};

  useEffect(() => {
    fetchSubUserData();
  }, [isLoggedIn()]);

  const fetchSubUserData = async () => {
    const response = await apiRequest(
      "SubUsers",
      `ReactGetSubUserDataByLink?token=${params.token}`,
      null,
      "POST"
    );

    if (response.status === "success") {
      if (response.data.BucketName) {
        // Redirect to custom domain if BucketName exist
        const newOrigin = response.data.BucketName;
        if (window.location.origin !== newOrigin) {
          window.location.href = `${newOrigin}${window.location.pathname}`;
          return;
        }
      }
      // There is error
      if (response.data.ErrorMessage) {
        setErrorMessage(response.data.ErrorMessage);
      }

      setUserDataProp({
        key: "EnforceStrongPass",
        value: response.data?.EnforceStrongPass,
      });

      // if profileLogo exist
      if (response.data.DownloadLogoImage) {
        setProfileLogo(response.data.DownloadLogoImage);
      } else {
        setProfileLogo("default");
      }

      // user doesn't has the same domain as the sub user list
      if (isLoggedIn() && !userData.UserName.includes(response.data.Domain)) {
        setErrorMessage("invitation-link-sub-user-different-domain");
      }

      setSubUsersData(response.data);
      setLoading(false);
    } else {
      setLoading(false);
      setProfileLogo("default");
      setErrorMessage("error-response");
    }
  };

  const completeAddSubUserByLink = async (email) => {
    setLoading(true);
    const parameters = {
      token: params.token,
    };
    const response = await apiRequest(
      "SubUsers",
      "ReactCompleteAddSubUserByLink",
      parameters,
      "POST"
    );

    setLoading(false);
    if (response.status === "success") {
      if (response.data) {
        setJoin(true);
      }
    }
  };

  const handleSignUp = () => {
    const isTokenExist = cookies.get(config.api.jumbomailApiAuthCookie);
    if (!isTokenExist || !isLoggedIn()) {
      setModal({
        modal: Authentication,
        modalSize: "full-modal",
        props: {
          type: "join-org",
          subUserDomain: subUsersData.Domain,
          subUserAuthOptions: subUsersData.AuthenticationOptions,
          onSubUserSubmitSuccess: completeAddSubUserByLink,
          enforceStrongPass: userData.EnforceStrongPass,
        },
      });
      setLoading(false);
      return;
    }
  };

  const logOutAndOpenLoginModal = () => {
    logout();
    setErrorMessage("");
    handleSignUp();
  };

  const OpenLoginModal = () => {
    if (!isLoggedIn()) {
      handleSignUp();
    } else {
      completeAddSubUserByLink();
    }
  };

  const getErrorMessage = () => {
    switch (errorMessage) {
      case "user-is-subscriber":
        const text = getTextByKey(
          userData.isTopUser
            ? "invitation-link-sub-user-is-owner"
            : "invitation-link-sub-user-is-subscriber"
        ).replace("#ownermail#", userData.UserName);

        return (
          <>
            {text}
            <Button medium onClick={() => logOutAndOpenLoginModal()}>
              {getTextByKey("change-account")}
            </Button>
          </>
        );
      case "user-in-sub-domain":
        return (
          <span>{getHtmlByKey("invitation-link-user-in-sub-domain")}</span>
        );
      case "user-in-this-sub-domain":
        // redirect to inbox
        window.location.href = config.baseurl + getPath() + "inbox";

        break;
      case "invitation-link-sub-user-different-domain":
        return (
          <>
            <span>
              {parse(
                getTextByKey("invitation-link-sub-user-different-domain")
                  .replace("#Domain#", userData.UserName)
                  .replace("#SubAccountDomain#", subUsersData.Domain)
              )}
            </span>
            <Button medium onClick={() => logOutAndOpenLoginModal()}>
              {getTextByKey("change-account")}
            </Button>
          </>
        );
      // return getTextByKey("invitation-link-sub-user-different-domain");
      default:
        return getHtmlByKey(errorMessage);
    }
  };

  if (errorMessage === "error-response") {
    return (
      <div className="invitation-card-wrapper">
        <Icon name="blocked" iconClass="blocked-icon" />
        <div className="invitation-sub-user-domain">
          {getHtmlByKey("wrong-invitation-link-title")}
        </div>
        <div className="invitation-sub-title">
          {getHtmlByKey("wrong-invitation-link-description")}
        </div>
        <ButtonA page="home" medium>
          {getTextByKey("back-to-home")}
        </ButtonA>
      </div>
    );
  }

  return (
    <div className="invitation-card-wrapper">
      {loading ? (
        <div className="loading-blog-container">
          <LoadingImageElement />
        </div>
      ) : (
        <>
          {subUsersData.ProfileImage && (
            <div className="top-user-logo">
              <Image src={subUsersData.ProfileImage} alt="sub-user-image" />
            </div>
          )}
          <div className="invitation-sub-title">
            {subUsersData.OrganizationName || subUsersData.UserFullName}{" "}
            {getTextByKey("invitation-sub-title")}
          </div>
          <div className="invitation-sub-user-domain">
            {subUsersData.Domain}
          </div>
          {errorMessage ? (
            <div className="invitation-sub-user-error">{getErrorMessage()}</div>
          ) : (
            <>
              <div className="invitation-sub-text">
                {getTextByKey("organization-email-adress-required-message")}
              </div>
              <InviteSubUserCheckboxAgreement
                onAgreementChange={(state) => setIsJoinAllowed(state)}
                agreementText={getHtmlByKey(
                  "invite-subuser-checkbox-general-link"
                )}
                agreementChecked={isJoinAllowed}
              />
              <Button
                onClick={() =>
                  isJoinAllowed ? OpenLoginModal() : setIsJoinAllowed(false)
                }
                medium
              >
                {getTextByKey("join")}
              </Button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default InvitationLinkCard;
